import {
  buildChildrensBookPhysicalPageItems,
  childrensBookWriterPageLabel,
} from "./childrensBookPhysicalPagination";

const clean = (value) => String(value ?? "").trim();
const kindFor = (unit) => unit?.unitType === "front_matter"
  ? "front" : unit?.unitType === "back_matter" ? "back" : "manuscript";

// Read-only projection, not a manuscript conversion. Never returns saved text
// or alters the book map. The optional outline is a separate, unsaved input.
export function buildChildrensBookChapterSceneNavigationV1({
  units = [], chapters = [],
} = {}) {
  const source = Array.isArray(units) ? units : [];
  const numbered = buildChildrensBookPhysicalPageItems(source);
  const seenUnits = new Set();
  const entries = [];
  for (const item of numbered) {
    const id = clean(item.unit?.id);
    if (!id || seenUnits.has(id)) {
      return { ok: false, code: "invalid_unit_identity", sections: [], totalUnits: 0 };
    }
    seenUnits.add(id);
    entries.push({
      unitId: id,
      sourceIndex: item.index,
      unitType: clean(item.unit?.unitType),
      title: clean(item.unit?.workingTitle),
      pageStart: item.pageStart,
      pageEnd: item.pageEnd,
      pageLabel: childrensBookWriterPageLabel(item),
      kind: kindFor(item.unit),
    });
  }
  const front = entries.filter(item => item.kind === "front");
  const back = entries.filter(item => item.kind === "back");
  const manuscript = entries.filter(item => item.kind === "manuscript");
  const safeSections = () => [
    ...(front.length ? [{ key: "front", kind: "front", entries: front }] : []),
    ...(manuscript.length ? [{ key: "unassigned", kind: "unassigned", entries: manuscript }] : []),
    ...(back.length ? [{ key: "back", kind: "back", entries: back }] : []),
  ];
  const outline = Array.isArray(chapters) ? chapters : [];
  if (outline.length === 0) {
    return { ok: true, mode: "page-only", totalUnits: entries.length, sections: safeSections() };
  }
  const byId = new Map(manuscript.map(item => [item.unitId, item]));
  const seenChapters = new Set();
  const seenScenes = new Set();
  const assigned = new Set();
  const organized = [];
  let previousIndex = -1;
  const invalid = () => ({
    ok: false, code: "invalid_chapter_scene_outline", mode: "page-only",
    totalUnits: entries.length, sections: safeSections(),
  });
  for (const chapter of outline) {
    const chapterId = clean(chapter?.id);
    const title = clean(chapter?.title);
    if (!chapterId || !title || seenChapters.has(chapterId) ||
        !Array.isArray(chapter?.scenes)) return invalid();
    seenChapters.add(chapterId);
    const scenes = [];
    for (const scene of chapter.scenes) {
      const sceneId = clean(scene?.id);
      const sceneTitle = clean(scene?.title);
      if (!sceneId || !sceneTitle || seenScenes.has(sceneId) ||
          !Array.isArray(scene?.unitIds) || scene.unitIds.length === 0) return invalid();
      seenScenes.add(sceneId);
      const sceneEntries = [];
      for (const rawId of scene.unitIds) {
        const unitId = clean(rawId);
        const item = byId.get(unitId);
        if (!item || assigned.has(unitId) || item.sourceIndex <= previousIndex) return invalid();
        previousIndex = item.sourceIndex;
        assigned.add(unitId);
        sceneEntries.push(item);
      }
      scenes.push({ key: sceneId, title: sceneTitle, entries: sceneEntries });
    }
    organized.push({ key: chapterId, kind: "chapter", title, scenes });
  }
  const unassigned = manuscript.filter(item => !assigned.has(item.unitId));
  return {
    ok: true, mode: "chapter-scene", totalUnits: entries.length,
    sections: [
      ...(front.length ? [{ key: "front", kind: "front", entries: front }] : []),
      ...organized,
      ...(unassigned.length ? [{ key: "unassigned", kind: "unassigned", entries: unassigned }] : []),
      ...(back.length ? [{ key: "back", kind: "back", entries: back }] : []),
    ],
  };
}
